import type { BackgroundFile, BackgroundSettings } from './index';
import type { CssResolvers, CssVars } from '../../css-vars';

import type { Device } from '../../utils';
import { DEVICE_SUFFIX, atDevice } from '../../utils';
import type { ScrollEffectMode } from '../scroll-effect/scroll-effect.css-vars';
import { scrollEffectMode } from '../scroll-effect/scroll-effect.css-vars';
import { colorValueVar } from '../../mixins';

/** The background variables the block and the column both emit. */
/** `EFilerOverlay.BLURRY` — the blur bleeds past the box, so the layer is outset. */
const BLURRY_FILTER = 'blur(3px)';
const BLUR_COMPENSATION = '-3px';

/** `rgba()` from a hex or rgb colour plus an opacity, for the overlay tint. */
export const toRgbaColor = (color: string, opacity: number): string => {
    let r: string | number;
    let g: string | number;
    let b: string | number;

    if (color.includes('rgb')) {
        const [red = 0, green = 0, blue = 0] = color.match(/\d+/g) ?? [];
        r = red;
        g = green;
        b = blue;
    } else {
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
    }

    return `rgba(${r},${g},${b},${opacity})`;
};

/**
 * The overlay as a gradient layer to put in front of the image, or `''`.
 * `tintDevice` lets the zero block read the tint off desktop for every
 * breakpoint, as its legacy styles did.
 */
export function backgroundTintLayer(background: BackgroundSettings | undefined, tintDevice: Device): string {
    const overlay = background?.overlay;
    const overlayColor = atDevice(overlay?.color, tintDevice);

    if (!atDevice(overlay?.isEnabled, tintDevice) || !overlayColor) {
        return '';
    }

    const rgba = toRgbaColor(overlayColor, atDevice(overlay?.opacity, tintDevice) ?? 0);

    return `linear-gradient(${rgba}, ${rgba}), `;
}

/** The picked image sits over the colour, optionally tinted by the overlay. */
export function backgroundVar(
    background: BackgroundSettings | undefined,
    { colorCss, imageCss }: CssResolvers,
    device: Device = 'desktop'
): string {
    const color = colorValueVar(atDevice(background?.color, device), colorCss);
    const image = atDevice(background?.image, device);

    if (!image) {
        return color;
    }

    return `${backgroundTintLayer(background, device)}${imageCss(image)}, ${color}`;
}

/** Position, repeat and size only carry a value once there is an image. */
export function backgroundLayerVar(
    background: BackgroundSettings | undefined,
    key: 'size' | 'position' | 'repeat',
    device: Device = 'desktop'
): string | undefined {
    return atDevice(background?.image, device) ? String(atDevice(background?.[key], device) ?? '') : undefined;
}

export function backgroundFilterVar(
    background: BackgroundSettings | undefined,
    device: Device = 'desktop'
): string | undefined {
    const filter = background?.filter;
    const value = atDevice(filter?.value, device);

    if (!atDevice(background?.image, device) || !atDevice(filter?.isEnabled, device) || !value) {
        return undefined;
    }

    return value;
}

/** The blur bleeds past the box, so its layer is outset to compensate. */
export function backgroundMarginVar(
    background: BackgroundSettings | undefined,
    device: Device = 'desktop'
): string | undefined {
    return backgroundFilterVar(background, device) === BLURRY_FILTER ? BLUR_COMPENSATION : undefined;
}

/**
 * `--<prefix>-bg`, and with an image also `-bg-position` / `-bg-repeat` /
 * `-bg-size`, plus `-bg-filter` / `-bg-margin` when a filter is on — each with
 * its `-tablet` / `-mobile` twin.
 */
export function backgroundVars(
    background: BackgroundSettings | undefined,
    prefix: string,
    resolvers: CssResolvers,
    device: Device = 'desktop'
): CssVars {
    const suffix = DEVICE_SUFFIX[device];
    const vars: CssVars = { [`--${prefix}-bg${suffix}`]: backgroundVar(background, resolvers, device) };

    if (!atDevice(background?.image, device)) {
        return vars;
    }

    // Past the guard above the image is present, so these always carry a value.
    vars[`--${prefix}-bg-position${suffix}`] = backgroundLayerVar(background, 'position', device) as string;
    vars[`--${prefix}-bg-repeat${suffix}`] = backgroundLayerVar(background, 'repeat', device) as string;
    vars[`--${prefix}-bg-size${suffix}`] = backgroundLayerVar(background, 'size', device) as string;

    const filter = backgroundFilterVar(background, device);
    if (filter) {
        vars[`--${prefix}-bg-filter${suffix}`] = filter;

        const margin = backgroundMarginVar(background, device);
        if (margin) {
            vars[`--${prefix}-bg-margin${suffix}`] = margin;
        }
    }

    return vars;
}

/**
 * The block's per-breakpoint scroll effect, as a mode — see `scrollEffectMode`.
 * A column's background has none; its scroll effect is its own feature.
 */
export function backgroundScrollEffectMode(
    background: BackgroundSettings | undefined,
    device: Device = 'desktop'
): ScrollEffectMode | null {
    const scrollEffect = background?.scrollEffect;

    return scrollEffectMode(
        scrollEffect
            ? {
                  isEnabled: !!atDevice(scrollEffect.isEnabled, device),
                  value: atDevice(scrollEffect.value, device) ?? '',
              }
            : undefined
    );
}
